import * as React from 'react';
import axios from 'axios';

import {
  Card,
  CardHeader,
  CardContent,
  Typography,
  Stack,
  Box,
  Divider,
  Chip,
  Avatar,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  FormHelperText,
  Button,
  Collapse,
  Tooltip,
  Snackbar,
  Alert,
  TextField,
} from '@mui/material';
import DevicesRoundedIcon from '@mui/icons-material/DevicesRounded';
import SettingsRoundedIcon from '@mui/icons-material/SettingsRounded';
import SaveRoundedIcon from '@mui/icons-material/SaveRounded';
import WifiIcon from '@mui/icons-material/Wifi';
import { useGrowSetup } from '../GrowSetupContext';

// Assets
import steckdose from '../assets/steckdose.png';
import port1 from '../assets/port1.png';
import port2 from '../assets/port2.png';
import port3 from '../assets/port3.png';
import automatisch from '../assets/automatik.png';
import intervall from '../assets/Intervall.png';

function StatusChip({ channel }) {
  const label = channel === '0' ? 'Steckdose' : `Port ${channel}`;
  const color = channel === '0' ? 'default' : 'success';
  return <Chip size="small" label={label} color={color} variant="filled" />;
}

function SelectOptionContent({ img, label }) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
      <Box
        sx={{
          width: 42,
          height: 28,
          borderRadius: 1.5,
          overflow: 'hidden',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: 'action.hover',
          border: '1px solid',
          borderColor: 'divider',
          flexShrink: 0,
        }}
      >
        <Box
          component="img"
          src={img}
          alt={label}
          sx={{
            width: '78%',
            height: '78%',
            objectFit: 'contain',
            display: 'block',
          }}
        />
      </Box>

      <Typography variant="body1">{label}</Typography>
    </Box>
  );
}

const CHANNEL_OPTIONS = [
  { value: '1', label: 'Port 1', img: port1 },
  { value: '2', label: 'Port 2', img: port2 },
  { value: '3', label: 'Port 3', img: port3 },
  { value: '0', label: 'Steckdose', img: steckdose },
];

const MODE_OPTIONS = [
  { value: 'auto', label: 'Automatisch', img: automatisch },
  { value: 'intervall', label: 'Intervall', img: intervall },
];

export default function ActorConfigCard(props) {
  const { grow_setup } = useGrowSetup();

  const [watt, setWatt] = React.useState(props.watt ?? 0);
  const [mode, setMode] = React.useState(props.rule_mode ?? 'auto');
  const [channel, setChannel] = React.useState(String(props.dimmer ?? '0'));
  const [ruleOn, setRuleOn] = React.useState(props.rule_intervall_on ?? 0);
  const [ruleOff, setRuleOff] = React.useState(props.rule_intervall_off ?? 0);
  const [saving, setSaving] = React.useState(false);
  const [pairingOpen, setPairingOpen] = React.useState(false);

  const isIntervall = mode === 'intervall';
  const isSteckdose = channel === '0';

  const dirty =
    watt !== (props.watt ?? 0) ||
    mode !== (props.rule_mode ?? 'auto') ||
    channel !== String(props.dimmer ?? '0') ||
    ruleOn !== (props.rule_intervall_on ?? 0) ||
    ruleOff !== (props.rule_intervall_off ?? 0);

  const selectedChannel =
    CHANNEL_OPTIONS.find((option) => option.value === channel) ?? CHANNEL_OPTIONS[0];

  const selectedMode =
    MODE_OPTIONS.find((option) => option.value === mode) ?? MODE_OPTIONS[0];

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.post(
        '/save_actor_config',
        {
          grow_setup_id: grow_setup,
          rule_mode: mode,
          watt,
          dimmer: channel,
          actor_id: props.actor_id,
          name: props.name,
          rule_intervall_on: ruleOn,
          rule_intervall_off: ruleOff,
        },
        {
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
            Authorization: `Bearer ${props.token}`,
          },
        }
      );

      props.onSaved?.();
    } finally {
      setSaving(false);
    }
  };

  const handlePairing = async () => {
    try {
      await axios.post(
        '/pairing_mode_on',
        {
          data: {
            grow_setup_id: grow_setup,
            actor_name: props.name,
          },
        },
        {
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
            Authorization: `Bearer ${props.token}`,
          },
        }
      );
    } finally {
      setPairingOpen(true);
    }
  };

  const handleClosePairingSnackbar = (_, reason) => {
    if (reason === 'clickaway') return;
    setPairingOpen(false);
  };

  const onChangeChannel = (val) => {
    const res = props.onTryChangeChannel?.(props.actor_id ?? props.id, parseInt(val, 10));
    if (res && res.ok === false) return;
    setChannel(val);
  };

  return (
    <Card
      variant="outlined"
      sx={{
        borderRadius: 4,
        width: '100%',
        maxWidth: 420,
        overflow: 'hidden',
        bgcolor: 'background.paper',
        borderColor: 'divider',
        boxShadow: '0 8px 30px rgba(0,0,0,0.18)',
      }}
    >
      <CardHeader
        sx={{ pb: 1.5 }}
        avatar={
          <Avatar
            sx={{
              width: 48,
              height: 48,
              bgcolor: 'action.selected',
            }}
          >
            <DevicesRoundedIcon />
          </Avatar>
        }
        title={
          <Stack direction="row" alignItems="center" gap={1} flexWrap="wrap">
            <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.1 }}>
              {props.name.charAt(0).toUpperCase() + props.name.slice(1)}
            </Typography>
            <StatusChip channel={channel} />
            {dirty && (
              <Chip
                size="small"
                color="warning"
                label="Ungespeichert"
                variant="filled"
              />
            )}
          </Stack>
        }
        subheader={
          <Stack direction="row" gap={1} alignItems="center" sx={{ mt: 0.5 }}>
            <SettingsRoundedIcon fontSize="small" />
            <Typography variant="body2" color="text.secondary">
              ID: {props.actor_id}
            </Typography>
          </Stack>
        }
      />

      <Divider />

      <CardContent sx={{ p: 2.5 }}>
        <Stack spacing={2.25}>
          <TextField
            size="small"
            fullWidth
            label="Nennleistung (W)"
            type="number"
            inputProps={{ min: 0, max: 4000, step: 10 }}
            value={watt}
            onChange={(e) => setWatt(Number(e.target.value ?? 0))}
            helperText="Verbrauch zur Lastabschätzung & Sicherheit"
            slotProps={{
              input: {
                endAdornment: (
                  <Typography variant="body2" color="text.secondary" sx={{ ml: 1 }}>
                    Watt
                  </Typography>
                ),
              },
            }}
          />

          <FormControl size="small" fullWidth>
            <InputLabel id={`channel-label-${props.actor_id}`}>Kanal</InputLabel>
            <Select
              labelId={`channel-label-${props.actor_id}`}
              label="Kanal"
              value={channel}
              onChange={(e) => onChangeChannel(e.target.value)}
              renderValue={() => (
                <SelectOptionContent
                  img={selectedChannel.img}
                  label={selectedChannel.label}
                />
              )}
              sx={{
                '& .MuiSelect-select': {
                  display: 'flex',
                  alignItems: 'center',
                  py: 1.1,
                },
              }}
            >
              {CHANNEL_OPTIONS.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  <SelectOptionContent img={option.img} label={option.label} />
                </MenuItem>
              ))}
            </Select>
            <FormHelperText>
              {isSteckdose
                ? 'Direkt an Steckdose geschaltet'
                : `Verbunden mit Port ${channel}`}
            </FormHelperText>
          </FormControl>

          <FormControl size="small" fullWidth>
            <InputLabel id={`mode-label-${props.actor_id}`}>Modus</InputLabel>
            <Select
              labelId={`mode-label-${props.actor_id}`}
              label="Modus"
              value={mode}
              onChange={(e) => setMode(e.target.value)}
              renderValue={() => (
                <SelectOptionContent img={selectedMode.img} label={selectedMode.label} />
              )}
              sx={{
                '& .MuiSelect-select': {
                  display: 'flex',
                  alignItems: 'center',
                  py: 1.1,
                },
              }}
            >
              {MODE_OPTIONS.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  <SelectOptionContent img={option.img} label={option.label} />
                </MenuItem>
              ))}
            </Select>
            <FormHelperText>
              {isIntervall
                ? 'Schaltet zyklisch nach Zeiten'
                : 'Regelt automatisch nach Sensorik'}
            </FormHelperText>
          </FormControl>

          <Collapse in={isIntervall} unmountOnExit>
            <Box
              sx={{
                p: 2,
                borderRadius: 3,
                border: '1px solid',
                borderColor: 'divider',
                bgcolor: 'action.hover',
              }}
            >
              <Stack direction={{ xs: 'column', sm: 'row' }} gap={2}>
                <TextField
                  size="small"
                  fullWidth
                  label="Aktiv (min)"
                  type="number"
                  inputProps={{ min: 0, max: 4300, step: 1 }}
                  value={ruleOn}
                  onChange={(e) => setRuleOn(Number(e.target.value ?? 0))}
                  helperText="Zeit EIN pro Zyklus"
                />
                <TextField
                  size="small"
                  fullWidth
                  label="Inaktiv (min)"
                  type="number"
                  inputProps={{ min: 0, max: 4300, step: 1 }}
                  value={ruleOff}
                  onChange={(e) => setRuleOff(Number(e.target.value ?? 0))}
                  helperText="Zeit AUS pro Zyklus"
                />
              </Stack>
            </Box>
          </Collapse>
        </Stack>
      </CardContent>

      <Divider />

      <Box
        sx={{
          p: 2,
          display: 'flex',
          gap: 1.5,
          flexWrap: 'wrap',
          alignItems: 'center',
        }}
      >
        <Tooltip title="Pairing starten">
          <span>
            <Button
              variant="contained"
              startIcon={<WifiIcon />}
              onClick={handlePairing}
              sx={{
                minWidth: 132,
                borderRadius: 2.5,
                textTransform: 'none',
                fontWeight: 600,
              }}
            >
              Pairing
            </Button>
          </span>
        </Tooltip>

        <Tooltip title={dirty ? 'Änderungen speichern' : 'Keine Änderungen'}>
          <span>
            <Button
              variant="contained"
              startIcon={<SaveRoundedIcon />}
              disabled={!dirty || saving}
              onClick={handleSave}
              sx={{
                minWidth: 148,
                borderRadius: 2.5,
                textTransform: 'none',
                fontWeight: 600,
              }}
            >
              {saving ? 'Speichern...' : 'Speichern'}
            </Button>
          </span>
        </Tooltip>
      </Box>

      <Snackbar
        open={pairingOpen}
        autoHideDuration={3000}
        onClose={handleClosePairingSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={handleClosePairingSnackbar}
          severity="success"
          variant="filled"
          sx={{ width: '100%' }}
        >
          Pairing gestartet
        </Alert>
      </Snackbar>
    </Card>
  );
}